import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';

import { usePublicConfig } from '@/shared/api/queries';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTelegram } from '@/shared/hooks/useTelegram';
import { cn } from '@/shared/lib/cn';
import { ParticleField } from '@/visual';

import styles from './AppLayout.module.css';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactElement;
}

/**
 * Иконки навигации нарисованы кольцами и линиями — тот же язык, что у
 * движка колец в референсе, вместо чужеродного icon-пака.
 */
const RingIcon = (): ReactElement => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="7.5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="12" cy="12" r="2.5" fill="currentColor" opacity="0.55" />
  </svg>
);

const BridgeIcon = (): ReactElement => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M3 16c3.5 0 3.5-8 7-8s3.5 8 7 8 4-3 4-3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <circle cx="10" cy="8" r="1.8" fill="currentColor" />
  </svg>
);

const GamesIcon = (): ReactElement => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false">
    <circle cx="8.5" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="15.5" cy="15" r="3.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const ProfileIcon = (): ReactElement => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false">
    <circle cx="12" cy="8.5" r="3.5" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const AdminIcon = (): ReactElement => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true" focusable="false">
    <rect x="4" y="4" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.4" />
    <rect x="13" y="4" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.4" />
    <rect x="4" y="13" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.4" />
    <rect x="13" y="13" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

/**
 * Одна вёрстка на все размеры: до 1024px навигация внизу, от 1024px — сбоку,
 * контент в --sg-max-w-wide и двухколоночный. Второго дерева компонентов нет
 * намеренно: два дерева расходятся при первом же изменении.
 */
export function AppLayout(): ReactElement {
  const { t } = useTranslation();
  const { data: config } = usePublicConfig();
  const { haptic } = useTelegram();
  const { isStaff } = useAuth();

  const items: NavItem[] = [
    { to: '/', labelKey: 'nav.field', icon: <RingIcon /> },
    { to: '/bridge', labelKey: 'nav.bridge', icon: <BridgeIcon /> },
  ];
  // Вкладка «Игры» скрывается целиком, а не блокируется: раздел-заглушка,
  // который нельзя открыть, раздражает сильнее его отсутствия (D-13).
  if (config?.games_enabled === true) {
    items.push({ to: '/games', labelKey: 'nav.games', icon: <GamesIcon /> });
  }
  items.push({ to: '/profile', labelKey: 'nav.profile', icon: <ProfileIcon /> });
  // У управляющего свой капитал и своя история — клиентский кабинет ему нужен
  // так же, как клиенту. Переход в админку — отдельной вкладкой, не редиректом.
  if (isStaff) {
    items.push({ to: '/admin', labelKey: 'nav.admin', icon: <AdminIcon /> });
  }

  return (
    <div className={styles.shell}>
      <div className={styles.field} aria-hidden="true">
        <ParticleField />
      </div>

      <nav className={styles.nav} aria-label={t('nav.field')}>
        <ul className={styles.navList}>
          {items.map((item) => (
            <li key={item.to} className={styles.navItem}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => cn(styles.navLink, isActive && styles.navLinkActive)}
                onClick={() => haptic('selection')}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span className={styles.navLabel}>{t(item.labelKey)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <main className={styles.main}>
        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
